import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy } from '@angular/core';
import { FormGroup, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import {
  AuxiliaryDataFieldSchemaInterface,
  AuxiliaryDataFieldType,
  AuxiliaryDataSearchDateOperation,
  AuxiliaryDataSearchIntegerOperation,
  AuxiliaryDataSearchStringOperation,
} from '@vendasta/account-group';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyInputModule, SelectInputOption, SelectInputOptionInterface } from '@vendasta/galaxy/input';
import {
  FilterControl,
  FilterControlComponent,
  FilterControlOptions,
  FilterControlSerializer,
  FilterModule,
} from '@vendasta/va-filter2';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, take } from 'rxjs/operators';
import { SchemaService } from '../../schema-service/schema.service';
import { FilterHandler } from '../auxiliary-data-accounts-table-filters.service';
import { chooseHandler, RendererSelectorComponent } from './render-selector.component';

// reads and writes the filtered account group id into the url
export class AuxiliaryDataFilterSerializer implements FilterControlSerializer {
  constructor(public control: AuxiliaryDataFilterControl) {}

  serialize(): any {
    return JSON.stringify(this.control.value);
  }

  deserialize(filterValue: string): void {
    const filterOp = JSON.parse(filterValue);
    this.control.setValue(filterOp);
  }
}

export type AuxiliaryDataFieldOperation =
  | AuxiliaryDataSearchStringOperation
  | AuxiliaryDataSearchDateOperation
  | AuxiliaryDataSearchIntegerOperation;

interface AuxiliaryDataFieldOperationFilter {
  value: AuxiliaryDataFieldOperation;
  label: string;
}

const OperationMap = new Map<AuxiliaryDataFieldType, AuxiliaryDataFieldOperationFilter[]>([
  [
    AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_STRING,
    [
      {
        value: AuxiliaryDataSearchStringOperation.STRING_EQUALS,
        label: 'Equals to',
      },
      {
        value: AuxiliaryDataSearchStringOperation.STRING_DOES_NOT_EQUAL,
        label: 'Does not equals to',
      },
    ],
  ],
  [
    AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DROPDOWN,
    [
      {
        value: AuxiliaryDataSearchStringOperation.STRING_EQUALS,
        label: 'Equals to',
      },
      {
        value: AuxiliaryDataSearchStringOperation.STRING_DOES_NOT_EQUAL,
        label: 'Does not equals to',
      },
    ],
  ],
  [
    AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_CURRENCY,
    [
      {
        value: AuxiliaryDataSearchIntegerOperation.INT_RANGE,
        label: 'Range',
      },
    ],
  ],
  [
    AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_INTEGER,
    [
      {
        value: AuxiliaryDataSearchIntegerOperation.INT_RANGE,
        label: 'Range',
      },
    ],
  ],
  [
    AuxiliaryDataFieldType.AUXILIARY_DATA_FIELD_TYPE_DATE,
    [
      {
        value: AuxiliaryDataSearchDateOperation.DATE_RANGE,
        label: 'Range',
      },
    ],
  ],
]);

@Component({
  standalone: true,
  template: `
    <ng-container *ngIf="options$ | async as options">
      <glxy-search-select-input
        label="Select custom field"
        [options]="options"
        (selectedOptions)="handleCustomFieldSelectOutput($event)"
        placeholder="Select custom field"
      ></glxy-search-select-input>
    </ng-container>
    <glxy-form-field>
      <glxy-label>Select operation</glxy-label>
      <mat-select name="operation" [(formControl)]="operationControl">
        <mat-option *ngFor="let operationOption of operationOptions" [value]="operationOption.value">
          {{ operationOption.label }}
        </mat-option>
      </mat-select>
    </glxy-form-field>
    <aux-renderer-selector [handler]="filterHandler"></aux-renderer-selector>
    <button mat-flat-button color="primary" (click)="applyFilter()">Apply</button>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    MatSelectModule,
    ReactiveFormsModule,
    GalaxyInputModule,
    GalaxyFormFieldModule,
    MatButtonModule,
    FilterModule,
    RendererSelectorComponent,
  ],
})
export class AuxiliaryDataFilterControlComponent implements FilterControlComponent, OnDestroy {
  control: AuxiliaryDataFilterControl;
  formGroup: FormGroup;
  fieldSchemas$: Observable<AuxiliaryDataFieldSchemaInterface[]>;
  options$: Observable<SelectInputOption[]>;
  accountGroupNameControl: UntypedFormControl = new UntypedFormControl('');
  subscriptions: Subscription[] = [];
  destroyed$$ = new Subject<void>();
  operationOptions: AuxiliaryDataFieldOperationFilter[];

  operationControl: UntypedFormControl = new UntypedFormControl('');

  private selectedFieldSchemas$$ = new BehaviorSubject<AuxiliaryDataFieldSchemaInterface>(null);
  selectedFieldSchema$ = this.selectedFieldSchemas$$.asObservable();

  filterHandler: FilterHandler;

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private readonly schemaService: SchemaService,
  ) {
    this.fieldSchemas$ = this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) => {
        return this.schemaService.listFieldSchemas('business', partnerId, false, '', 100);
      }),
      map((response) => response?.fieldSchemas || []),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.options$ = this.fieldSchemas$.pipe(
      map((fieldSchemas) =>
        fieldSchemas.map((f) => ({
          value: f.fieldId,
          label: f.fieldName,
          description: f.fieldDescription,
          selected: false,
          disabled: false,
        })),
      ),
    );
    this.subscriptions.push(
      this.selectedFieldSchema$.subscribe((fieldSchema) => {
        if (fieldSchema) {
          this.operationOptions = OperationMap.get(fieldSchema.fieldType);
          this.operationControl.setValue(this.operationOptions[0]?.value || '');
          this.filterHandler = chooseHandler(fieldSchema);
        }
      }),
    );
  }

  handleCustomFieldSelectOutput(selectInputOption: SelectInputOptionInterface): void {
    this.subscriptions.push(
      this.fieldSchemas$.pipe(take(1)).subscribe((fieldSchemas) => {
        const selectedSchema = fieldSchemas.find((f) => f.fieldId === selectInputOption.value);
        this.selectedFieldSchemas$$.next(selectedSchema);
      }),
    );
  }

  applyFilter(): void {
    const filterOperation = this.filterHandler.filterOperation(this.operationControl.value);
    this.control.setValue([filterOperation]);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}

export class AuxiliaryDataFilterControl extends FilterControl {
  component = AuxiliaryDataFilterControlComponent;
  serializer = new AuxiliaryDataFilterSerializer(this);
  constructor(public name: string, public label?: string, formState?: any, opts: FilterControlOptions = {}) {
    super(name, label, formState, opts);
  }
}
